/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal */

if (!customElements.get('floating-popup-button')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class FloatingPopupButton extends HTMLElement {
      constructor() {
        super();
        this.modal = this.nextElementSibling;
        this.button = this.querySelector('.floating-popup-button__trigger');
        this.isDragging = false;
        this.hasDragged = false;
        this.dragThreshold = 5;
        this.startX = 0;
        this.startY = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.offsetX = 0;
        this.offsetY = 0;
        this.positionKey = `floating-popup-position-${this.id}`;
        this.dismissKey = `floating-popup-dismissed-${this.id}`;
        this.isSamePosition = false;

        this.init();
      }

      init() {
        if (!this.button || !this.modal) return;

        // Check if popup was dismissed
        if (this.dataset.dismissible === 'true' && getCookie(this.dismissKey)) {
          this.style.display = 'none';
          return;
        }

        // Set initial position
        this.setInitialPosition();

        // Add event listeners
        this.button.addEventListener('click', this.handleButtonClick.bind(this));
        
        // Add dismiss button event listener if dismissible
        if (this.dataset.dismissible === 'true') {
          const dismissBtn = this.querySelector('.floating-popup-button__dismiss');
          if (dismissBtn) {
            dismissBtn.addEventListener('click', this.handleDismissClick.bind(this));
          }
        }
        
        // Add drag functionality if enabled
        if (this.dataset.draggable === 'true') {
          this.initDragFunctionality();
        }


        // Show button with animation
        requestAnimationFrame(() => {
          this.classList.add('floating-popup-button--visible');
        });

        // Handle Shopify theme editor
        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) {
              this.modal.open();
            }
          });
          document.addEventListener('shopify:section:deselect', () => {
            this.modal.close();
          });
        }
      }

      setInitialPosition() {
        // Try to get saved position first
        const savedPosition = localStorage.getItem(this.positionKey);
        
        if (savedPosition) {
          try {
            const { x, y } = JSON.parse(savedPosition);
            this.setPosition(x, y, false);
            return;
          } catch (e) {
            // Invalid saved position, fall back to default
          }
        }

        // Set default position based on settings
        const position = this.dataset.position || 'bottom-center';
        const margin = parseInt(this.dataset.margin) ?? 20;
        
        switch (position) {
          case 'left-center':
            this.setPosition(margin, (window.innerHeight - this.offsetHeight) / 2);
            break;
          case 'right-center':
            this.setPosition(window.innerWidth - this.offsetWidth - margin, (window.innerHeight - this.offsetHeight) / 2);
            break;
          case 'bottom-center':
          default:
            this.setPosition(
              (window.innerWidth - this.offsetWidth) / 2,
              window.innerHeight - this.offsetHeight - margin
            );
            break;
        }
      }

      /**
       * Get the effective dimensions of the element after rotation
       * @returns {Object} Object with width and height properties
       */
      getRotatedDimensions() {
        const position = this.dataset.position;
        const isRotated = position === 'left-center' || position === 'right-center';
        
        if (isRotated) {
          // When rotated 90 degrees, width and height are swapped
          return {
            width: this.offsetHeight,
            height: this.offsetWidth
          };
        }
        
        return {
          width: this.offsetWidth,
          height: this.offsetHeight
        };
      }

      setPosition(x, y, useMargin = true) {
        // Get the actual visual dimensions after rotation
        const dimensions = this.getRotatedDimensions();
        const margin = useMargin ? (parseInt(this.dataset.margin) ?? 20) : 0;
        
        // Constrain to viewport bounds using rotated dimensions
        const maxX = window.innerWidth - dimensions.width - margin;
        const maxY = window.innerHeight - dimensions.height - margin;
        
        x = Math.max(margin, Math.min(x, maxX));
        y = Math.max(margin, Math.min(y, maxY));
        
        // Adjust positioning for flush alignment on rotated buttons
        const position = this.dataset.position;
        let adjustedX = x;
        
        if (margin === 0 && (position === 'left-center' || position === 'right-center')) {
          // Calculate adjustment needed to account for rotation
          const adjustment = (this.offsetWidth - this.offsetHeight) / 2;

          if(localStorage.getItem(this.positionKey) == null) {
            if (position === 'left-center') {
              adjustedX = x - adjustment;
            } else if (position === 'right-center') {
              adjustedX = x + adjustment;
            }
          } else {
            if (position === 'left-center' || position === 'right-center') {
              adjustedX = x - adjustment;
            }
          }
        }
        // Use standard left/top positioning
        if(localStorage.getItem(this.positionKey) != null) {
          var isEndPos = adjustedX;
          if(window.innerWidth > 768 && x > (window.innerWidth / 2) && adjustedX >= isEndPos) {
            adjustedX = isEndPos;
          }
        }
        this.style.left = `${adjustedX}px`;
        this.style.top = `${y}px`;
        this.style.right = 'auto';
        this.style.bottom = 'auto';
        
        this.currentX = x;
        this.currentY = y;
      }

      savePosition() {
        try {
          localStorage.setItem(this.positionKey, JSON.stringify({
            x: this.currentX,
            y: this.currentY
          }));
        } catch (e) {
          // localStorage not available or full
        }
      }

      initDragFunctionality() {
        // Mouse events
        this.button.addEventListener('mousedown', this.handleDragStart.bind(this));
        document.addEventListener('mousemove', this.handleDragMove.bind(this));
        document.addEventListener('mouseup', this.handleDragEnd.bind(this));

        // Touch events
        if (theme.device.hasTouch) {
          this.button.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
          document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
          document.addEventListener('touchend', this.handleTouchEnd.bind(this));
        }

        // Prevent text selection during drag
        this.button.style.userSelect = 'none';
        this.button.style.webkitUserSelect = 'none';
      }

      handleButtonClick(evt) {
        // Don't open modal if we just finished dragging
        if (this.isDragging || this.hasDragged) {
          evt.preventDefault();
          return;
        }
        
        this.modal.open(this.button);
      }

      handleDragStart(evt) {
        if (this.dataset.draggable !== 'true') return;
        
        evt.preventDefault();
        this.startX = evt.clientX - this.currentX;
        this.startY = evt.clientY - this.currentY;
        this.button.classList.add('floating-popup-button--dragging');
      }

      handleDragMove(evt) {
        if (this.dataset.draggable !== 'true' || !this.button.classList.contains('floating-popup-button--dragging')) return;
        
        evt.preventDefault();
        
        const newX = evt.clientX - this.startX;
        const newY = evt.clientY - this.startY;
        
        // Check if we've moved beyond the drag threshold
        const deltaX = Math.abs(newX - this.currentX);
        const deltaY = Math.abs(newY - this.currentY);
        
        if (deltaX > this.dragThreshold || deltaY > this.dragThreshold) {
          this.hasDragged = true;
          this.isDragging = true;
        }
        this.isSamePosition = false;
        this.setPosition(newX, newY, false);
      }

      handleDragEnd() {
        if (this.dataset.draggable !== 'true') return;
        
        this.button.classList.remove('floating-popup-button--dragging');
        
        if (this.hasDragged) {
          this.savePosition();
          
          // Reset dragging state after a delay to prevent accidental clicks
          setTimeout(() => {
            this.isDragging = false;
            this.hasDragged = false;
          }, 150);
        } else {
          // Reset immediately if no drag occurred
          this.isDragging = false;
          this.hasDragged = false;
        }
      }

      handleTouchStart(evt) {
        if (this.dataset.draggable !== 'true') return;
        
        evt.preventDefault();
        const touch = evt.touches[0];
        this.startX = touch.clientX - this.currentX;
        this.startY = touch.clientY - this.currentY;
        this.button.classList.add('floating-popup-button--dragging');
      }

      handleTouchMove(evt) {
        if (this.dataset.draggable !== 'true' || !this.button.classList.contains('floating-popup-button--dragging')) return;
        
        evt.preventDefault();
        const touch = evt.touches[0];
        
        const newX = touch.clientX - this.startX;
        const newY = touch.clientY - this.startY;
        
        // Check if we've moved beyond the drag threshold
        const deltaX = Math.abs(newX - this.currentX);
        const deltaY = Math.abs(newY - this.currentY);
        
        if (deltaX > this.dragThreshold || deltaY > this.dragThreshold) {
          this.hasDragged = true;
          this.isDragging = true;
        }
        this.isSamePosition = false;
        this.setPosition(newX, newY, false);
      }

      handleTouchEnd() {
        if (this.dataset.draggable !== 'true') return;
        
        this.button.classList.remove('floating-popup-button--dragging');
        
        if (this.hasDragged) {
          this.savePosition();
          
          // Reset dragging state after a delay to prevent accidental clicks
          setTimeout(() => {
            this.isDragging = false;
            this.hasDragged = false;
          }, 150);
        } else {
          // Reset immediately if no drag occurred
          this.isDragging = false;
          this.hasDragged = false;
        }
      }

      handleDismissClick(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.handleDismiss();
      }

      handleDismiss() {
        if (this.dataset.dismissible === 'true') {
          const days = parseInt(this.dataset.dismissDays) || 30;
          setCookie(this.dismissKey, 'true', days);
          this.style.display = 'none';
        }
      }

      // Handle window resize
      connectedCallback() {
        this.resizeHandler = () => {
          // Reposition button to stay within bounds
          if(this.dataset.draggable == 'true') {
            console.log("this.currentX", this.currentX, window.innerWidth);
            if((this.currentX + 40) > window.innerWidth) {
              this.isSamePosition = true;
            }
            if(this.isSamePosition) {
              this.currentX = window.innerWidth - 40;
            }
            this.setPosition(this.currentX, this.currentY);
          } else {
            const position = this.dataset.position || 'bottom-center';
            const margin = parseInt(this.dataset.margin) ?? 20;
        
            switch (position) {
              case 'left-center':
                this.setPosition(margin, (window.innerHeight - this.offsetHeight) / 2);
                break;
              case 'right-center':
                this.setPosition(window.innerWidth - this.offsetWidth - margin, (window.innerHeight - this.offsetHeight) / 2);
                break;
              case 'bottom-center':
              default:
                this.setPosition(
                  (window.innerWidth - this.offsetWidth) / 2,
                  window.innerHeight - this.offsetHeight - margin
                );
                break;
            }
          }
        };
        
        window.addEventListener('resize', () => {
          this.resizeHandler();
        });
      }

      disconnectedCallback() {
        if (this.resizeHandler) {
          window.removeEventListener('resize', () => {
            this.resizeHandler();
          });
        }
      }
    }

    customElements.define('floating-popup-button', FloatingPopupButton);
  });
}